import { Suspense } from "react";
import { lazyWithRecovery as lazy, RouteRecovery, RouteLoader } from "@/components/RouteRecovery";
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import FeatureGate from "@/components/FeatureGate";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import DataSecurity from "@/pages/DataSecurity";
const Signup = lazy(() => import("@/pages/Signup"));
const Dashboard = lazy(() => import("@/pages/DashboardV2"));
const FamilyTree = lazy(() => import("@/pages/FamilyTree"));
const MembersDirectory = lazy(() => import("@/pages/MembersDirectory"));
const AddMember = lazy(() => import("@/pages/AddMember"));
const RecordStory = lazy(() => import("@/pages/RecordStory"));
const PersonProfile = lazy(() => import("@/pages/PersonProfile"));
const MemberProfileRedirect = lazy(() => import("@/pages/MemberProfileRedirect"));
const PrivacyOnboarding = lazy(() => import("@/pages/PrivacyOnboarding"));
const Vault = lazy(() => import("@/pages/Vault"));
const Settings = lazy(() => import("@/pages/Settings"));
const Memories = lazy(() => import("@/pages/Memories"));
const FamilyStatistics = lazy(() => import("@/pages/FamilyStatistics"));
const Timeline = lazy(() => import("@/pages/Timeline"));
const Reminders = lazy(() => import("@/pages/Reminders"));
const RecycleBin = lazy(() => import("@/pages/RecycleBin"));
const FamilyPoster = lazy(() => import("@/pages/FamilyPoster"));
const Culture = lazy(() => import("@/pages/Culture"));
const LegalVault = lazy(() => import("@/pages/LegalVault"));
const Celebrations = lazy(() => import("@/pages/Celebrations"));
const MemoryCapsule = lazy(() => import("@/pages/MemoryCapsule"));
const VideoMemories = lazy(() => import("@/pages/VideoMemories"));
const EmbeddedService = lazy(() => import("@/pages/EmbeddedService"));
const RequestForm = lazy(() => import("@/pages/RequestForm"));
const MyRequests = lazy(() => import("@/pages/MyRequests"));
const Achievements = lazy(() => import("@/pages/Achievements"));
const ActivityFeed = lazy(() => import("@/pages/ActivityFeed"));
const Plans = lazy(() => import("@/pages/Plans"));
const FamilyHealth = lazy(() => import("@/pages/FamilyHealth"));
const HealthRepository = lazy(() => import("@/pages/HealthRepository"));
const BloodGroupRegistry = lazy(() => import("@/pages/BloodGroupRegistry"));
const Downloads = lazy(() => import("@/pages/Downloads"));
const LegacyVault = lazy(() => import("@/pages/LegacyVault"));
const Marriages = lazy(() => import("@/pages/Marriages"));
const InviteFamily = lazy(() => import("@/pages/InviteFamily"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const Copyright = lazy(() => import("@/pages/Copyright"));
const AiUsageDisclaimer = lazy(() => import("@/pages/AiUsageDisclaimer"));
const BharatLegacyFoundation = lazy(() => import("@/pages/BharatLegacyFoundation"));
const DigitalLegacyNominee = lazy(() => import("@/pages/DigitalLegacyNominee"));
const IncidentResponse = lazy(() => import("@/pages/IncidentResponse"));
const LegacyAccessSocial = lazy(() => import("@/pages/LegacyAccessSocial"));
const ProductServices = lazy(() => import("@/pages/ProductServices"));
const SocialMediaPages = lazy(() => import("@/pages/SocialMediaPages"));
const TreeOtherSupport = lazy(() => import("@/pages/TreeOtherSupport"));
const UserContentOwnership = lazy(() => import("@/pages/UserContentOwnership"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const FounderLetter = lazy(() => import("@/pages/FounderLetter"));
const MadeInIndia = lazy(() => import("@/pages/MadeInIndia"));
const DigitalInheritance = lazy(() => import("@/pages/DigitalInheritance"));
const EthicalAI = lazy(() => import("@/pages/EthicalAI"));
const NomineeAccess = lazy(() => import("@/pages/NomineeAccess"));
const CustomerAwareness = lazy(() => import("@/pages/CustomerAwareness"));
const CustomerConsent = lazy(() => import("@/pages/CustomerConsent"));
const DPDPCompliance = lazy(() => import("@/pages/DPDPCompliance"));
const DataEncryption = lazy(() => import("@/pages/DataEncryption"));

const DataProtection = lazy(() => import("@/pages/DataProtection"));
const FamilyDispute = lazy(() => import("@/pages/FamilyDispute"));
const TreeMerging = lazy(() => import("@/pages/TreeMerging"));
const NomineeForm = lazy(() => import("@/pages/NomineeForm"));
const TransferOnDeath = lazy(() => import("@/pages/TransferOnDeath"));
const RecordDeletion = lazy(() => import("@/pages/RecordDeletion"));
const PremiumServices = lazy(() => import("@/pages/PremiumServices"));
const EscalationMatrix = lazy(() => import("@/pages/EscalationMatrix"));
const DataPortability = lazy(() => import("@/pages/DataPortability"));
const DeceasedTransition = lazy(() => import("@/pages/DeceasedTransition"));
const SecurityNotice = lazy(() => import("@/pages/SecurityNotice"));
const ExpertWriterTnc = lazy(() => import("@/pages/ExpertWriterTnc"));
const TreeAssistancePricing = lazy(() => import("@/pages/TreeAssistancePricing"));
const TreeAssistanceTnc = lazy(() => import("@/pages/TreeAssistanceTnc"));
const TreeOtherServices = lazy(() => import("@/pages/TreeOtherServices"));
const HistoryResearchTnc = lazy(() => import("@/pages/HistoryResearchTnc"));
const ExpertWriterWorkflow = lazy(() => import("@/pages/ExpertWriterWorkflow"));
const WriterNda = lazy(() => import("@/pages/WriterNda"));
const DisasterRecovery = lazy(() => import("@/pages/DisasterRecovery"));
const DataBreachResponse = lazy(() => import("@/pages/DataBreachResponse"));
const CustomerJourney = lazy(() => import("@/pages/CustomerJourney"));
const LegalDisclaimer = lazy(() => import("@/pages/LegalDisclaimer"));
const AiDisclaimerPage = lazy(() => import("@/pages/AiDisclaimerPage"));
const SecurityChecklist = lazy(() => import("@/pages/SecurityChecklist"));
const PlatformComparison = lazy(() => import("@/pages/PlatformComparison"));
const Uvps = lazy(() => import("@/pages/Uvps"));
const Mission = lazy(() => import("@/pages/Mission"));
const Vision = lazy(() => import("@/pages/Vision"));
const ModelWill = lazy(() => import("@/pages/ModelWill"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const DataRetention = lazy(() => import("@/pages/DataRetention"));
const MinorDataPolicy = lazy(() => import("@/pages/MinorDataPolicy"));
const AccountRecovery = lazy(() => import("@/pages/AccountRecovery"));
const SharedBiography = lazy(() => import("@/pages/SharedBiography"));
const Careers = lazy(() => import("@/pages/Careers"));
const ManagementTeam = lazy(() => import("@/pages/ManagementTeam"));
const DevelopmentTeam = lazy(() => import("@/pages/DevelopmentTeam"));
const BusinessAssociates = lazy(() => import("@/pages/BusinessAssociates"));
const FamilyCalendar = lazy(() => import("@/pages/FamilyCalendar"));
const FamilyHub = lazy(() => import("@/pages/FamilyHub"));
const FamilyRecords = lazy(() => import("@/pages/FamilyRecords"));
const ComingSoon = lazy(() => import("@/pages/ComingSoon"));
const PublicInfoPage = lazy(() => import("@/pages/PublicInfoPage"));
const AIFamilyAdvisor = lazy(() => import("@/pages/AIFamilyAdvisor"));
const AIFamilyStory = lazy(() => import("@/pages/AIFamilyStory"));
const SupportCenter = lazy(() => import("@/pages/SupportCenter"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
import { BiometricGate } from "@/components/BiometricGate";

// Sensitive routes that are wrapped with BiometricGate (passkey re-auth).
const GATED_PATHS = new Set(["/vault", "/legal-vault"]);
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminFamilies = lazy(() => import("@/pages/admin/AdminFamilies"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/AdminSubscriptions"));
const AdminCashfree = lazy(() => import("@/pages/admin/AdminCashfree"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const AdminPackages = lazy(() => import("@/pages/admin/AdminPackages"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminTickets = lazy(() => import("@/pages/admin/AdminTickets"));
const AdminSecurity = lazy(() => import("@/pages/admin/AdminSecurity"));
const AdminBroadcasts = lazy(() => import("@/pages/admin/AdminBroadcasts"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminExports = lazy(() => import("@/pages/admin/AdminExports"));
const AdminFamilyContent = lazy(() => import("@/pages/admin/AdminFamilyContent"));
const AdminTeam = lazy(() => import("@/pages/admin/AdminTeam"));
const AdminUxAudit = lazy(() => import("@/pages/admin/AdminUxAudit"));
const AdminSecurityAudit = lazy(() => import("@/pages/admin/AdminSecurityAudit"));
const AdminI18n = lazy(() => import("@/pages/admin/AdminI18n"));
const AdminUserPages = lazy(() => import("@/pages/admin/AdminUserPages"));
const AdminLandingSlides = lazy(() => import("@/pages/admin/AdminLandingSlides"));
const AdminApiKeys = lazy(() => import("@/pages/admin/AdminApiKeys"));
const AdminCRM = lazy(() => import("@/pages/admin/AdminCRM"));
const SMSAdmin = lazy(() => import("@/pages/admin/SMSAdmin"));
const AdminSiteAssets = lazy(() => import("@/pages/admin/AdminSiteAssets"));
const AdminContentManagers = lazy(() => import("@/pages/admin/AdminContentManagers"));
const AdminMessageTemplates = lazy(() => import("@/pages/admin/AdminMessageTemplates"));
const AdminIntegrations = lazy(() => import("@/pages/admin/AdminIntegrations"));
const AdminWhatsAppTemplates = lazy(() => import("@/pages/admin/AdminWhatsAppTemplates"));
const AdminWhatsAppTesting = lazy(() => import("@/pages/admin/AdminWhatsAppTesting"));
const AdminAskFR = lazy(() => import("@/pages/admin/AdminAskFR"));
const AdminForms = lazy(() => import("@/pages/admin/AdminForms"));
const AdminPopups = lazy(() => import("@/pages/admin/AdminPopups"));
const AdminShowcase = lazy(() => import("@/pages/admin/AdminShowcase"));
const AdminAIAnalytics = lazy(() => import("@/pages/admin/AdminAIAnalytics"));
const AdminAdAnalytics = lazy(() => import("@/pages/admin/AdminAdAnalytics"));
const AdminUtmBuilder = lazy(() => import("@/pages/admin/AdminUtmBuilder"));
const AdminTwilioWhatsApp = lazy(() => import("@/pages/admin/AdminTwilioWhatsApp"));
const AdminWhatsAppLog = lazy(() => import("@/pages/admin/AdminWhatsAppLog"));
const AdminWhatsAppCampaign = lazy(() => import("@/pages/admin/AdminWhatsAppCampaign"));
const AdminActivityLog = lazy(() => import("@/pages/admin/AdminActivityLog"));
const AdminFlipbooks = lazy(() => import("@/pages/admin/AdminFlipbooks"));
const FamilyWorldMap = lazy(() => import("@/pages/FamilyWorldMap"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Profile = lazy(() => import("@/pages/Profile"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const AiChatPage = lazy(() => import("@/pages/AiChatPage"));
const Features = lazy(() => import("@/pages/Features"));
const DemosPage = lazy(() => import("@/pages/DemosPage"));
const UseCases = lazy(() => import("@/pages/UseCases"));
const ConnectFriends = lazy(() => import("@/pages/ConnectFriends"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const TimeCapsule = lazy(() => import("@/pages/TimeCapsule"));
const Franchise = lazy(() => import("@/pages/Franchise"));
const NgoPartnership = lazy(() => import("@/pages/NgoPartnership"));
const CommunityService = lazy(() => import("@/pages/CommunityService"));
const ProfessionalAssociate = lazy(() => import("@/pages/ProfessionalAssociate"));
const FamilyWorkspace = lazy(() => import("@/pages/FamilyWorkspace"));
const AiSmartSearch = lazy(() => import("@/pages/AiSmartSearch"));
const AiFamilyInsights = lazy(() => import("@/pages/AiFamilyInsights"));

const USER_PAGES = [
    ["/dashboard", Dashboard],
    ["/connect-friends", ConnectFriends],
    ["/family-tree", FamilyTree],
    ["/members", MembersDirectory],
    ["/add-member", AddMember],
    ["/memories", Memories],
    ["/privacy-onboarding", PrivacyOnboarding],
    ["/vault", Vault],
    ["/settings", Settings],
    ["/activity-log", ActivityLog],
    ["/payment/result", PaymentResult],
    ["/profile", Profile],
    ["/person/:id", PersonProfile],
    ["/member-profile", MemberProfileRedirect],
    ["/record-story", RecordStory],
    ["/statistics", FamilyStatistics],
    ["/timeline", Timeline],
    ["/reminders", Reminders],
    ["/recycle-bin", RecycleBin],
    ["/poster", FamilyPoster],
    ["/culture", Culture],
    ["/legal-vault", LegalVault],
    ["/celebrations", Celebrations],
    ["/capsule", MemoryCapsule],
    ["/video-memories", VideoMemories],
    ["/my-requests", MyRequests],
    ["/achievements", Achievements],
    ["/activity", ActivityFeed],
    ["/plans", Plans],
    ["/family-health", FamilyHealth],
    ["/health-repository", HealthRepository],
    ["/blood-group-registry", BloodGroupRegistry],
    ["/downloads", Downloads],
    ["/legacy-vault", LegacyVault],
    ["/marriages", Marriages],
    ["/invite", InviteFamily],
    ["/family-world-map", FamilyWorldMap],
    ["/workspace", FamilyWorkspace],
    ["/ai/search", AiSmartSearch],
    ["/ai/insights", AiFamilyInsights],
];

const ADMIN_PAGES = [
    ["families", AdminFamilies],
    ["users", AdminUsers],
    ["subscriptions", AdminSubscriptions],
    ["cashfree", AdminCashfree],
    ["packages", AdminPackages],
    ["content", AdminContent],
    ["family-content", AdminFamilyContent],
    ["exports", AdminExports],
    ["analytics", AdminAnalytics],
    ["tickets", AdminTickets],
    ["security", AdminSecurity],
    ["broadcasts", AdminBroadcasts],
    ["settings", AdminSettings],
    ["team", AdminTeam],
    ["ux-audit", AdminUxAudit],
    ["security-audit", AdminSecurityAudit],
    ["i18n", AdminI18n],
    ["user-pages", AdminUserPages],
    ["landing-slides", AdminLandingSlides],
    ["api-keys", AdminApiKeys],
    ["crm", AdminCRM],
    ["sms", SMSAdmin],
    ["site-assets", AdminSiteAssets],
    ["content-managers", AdminContentManagers],
    ["message-templates", AdminMessageTemplates],
    ["integrations", AdminIntegrations],
    ["whatsapp-templates", AdminWhatsAppTemplates],
    ["whatsapp-testing", AdminWhatsAppTesting],
    ["ask-fr", AdminAskFR],
    ["forms", AdminForms],
    ["popups", AdminPopups],
    ["showcase", AdminShowcase],
    ["ai-analytics", AdminAIAnalytics],
    ["ad-analytics", AdminAdAnalytics],
    ["utm-builder", AdminUtmBuilder],
    ["twilio-whatsapp", AdminTwilioWhatsApp],
    ["whatsapp-log", AdminWhatsAppLog],
    ["whatsapp-campaign", AdminWhatsAppCampaign],
    ["activity-log", AdminActivityLog],
    ["flipbooks", AdminFlipbooks],
];


export default function AppRoutes() {
    return (
        <RouteRecovery>
        <Suspense fallback={<RouteLoader />}>
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/data-security" element={<DataSecurity />} />
            <Route path="/copyright" element={<Copyright />} />
            <Route path="/ai-usage-disclaimer" element={<AiUsageDisclaimer />} />
            <Route path="/bharat-legacy-foundation" element={<BharatLegacyFoundation />} />
            <Route path="/digital-legacy-nominee" element={<DigitalLegacyNominee />} />
            <Route path="/incident-response" element={<IncidentResponse />} />
            <Route path="/legacy-access-social" element={<LegacyAccessSocial />} />
            <Route path="/product-services" element={<ProductServices />} />
            <Route path="/social-media" element={<SocialMediaPages />} />
            <Route path="/tree-other-support" element={<TreeOtherSupport />} />
            <Route path="/user-content-ownership" element={<UserContentOwnership />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/founder-letter" element={<FounderLetter />} />
            <Route path="/made-in-india" element={<MadeInIndia />} />
            <Route path="/digital-inheritance" element={<DigitalInheritance />} />
            <Route path="/ethical-ai" element={<EthicalAI />} />
            <Route path="/nominee-access" element={<NomineeAccess />} />
            <Route path="/customer-awareness" element={<CustomerAwareness />} />
            <Route path="/customer-consent" element={<CustomerConsent />} />
            <Route path="/dpdp-compliance" element={<DPDPCompliance />} />
            <Route path="/data-encryption" element={<DataEncryption />} />
            <Route path="/data-protection" element={<DataProtection />} />
            <Route path="/family-dispute" element={<FamilyDispute />} />
            <Route path="/tree-merging" element={<TreeMerging />} />
            <Route path="/nominee-form" element={<NomineeForm />} />
            <Route path="/transfer-on-death" element={<TransferOnDeath />} />
            <Route path="/record-deletion" element={<RecordDeletion />} />
            <Route path="/premium-services" element={<PremiumServices />} />
            <Route path="/escalation-matrix" element={<EscalationMatrix />} />
            <Route path="/data-portability" element={<DataPortability />} />
            <Route path="/deceased-transition" element={<DeceasedTransition />} />
            <Route path="/security-notice" element={<SecurityNotice />} />
            <Route path="/expert-writer-tnc" element={<ExpertWriterTnc />} />
            <Route path="/tree-assistance-pricing" element={<TreeAssistancePricing />} />
            <Route path="/tree-assistance-tnc" element={<TreeAssistanceTnc />} />
            <Route path="/tree-other-services" element={<TreeOtherServices />} />
            <Route path="/history-research-tnc" element={<HistoryResearchTnc />} />
<Route path="/expert-writer-workflow" element={<ExpertWriterWorkflow />} />
            <Route path="/writer-nda" element={<WriterNda />} />
            <Route path="/disaster-recovery" element={<DisasterRecovery />} />
            <Route path="/data-breach-response" element={<DataBreachResponse />} />
            <Route path="/customer-journey" element={<CustomerJourney />} />
            <Route path="/legal-disclaimer" element={<LegalDisclaimer />} />
            <Route path="/ai-content-disclaimer" element={<AiDisclaimerPage />} />
            <Route path="/security-checklist" element={<SecurityChecklist />} />
            <Route path="/platform-comparison" element={<PlatformComparison />} />
            <Route path="/uvps" element={<Uvps />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/vision" element={<Vision />} />
            <Route path="/model-will" element={<ModelWill />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/data-retention" element={<DataRetention />} />
            <Route path="/minor-data-policy" element={<MinorDataPolicy />} />
            <Route path="/account-recovery" element={<AccountRecovery />} />
            <Route path="/share/biography/:token" element={<SharedBiography />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/team" element={<ManagementTeam />} />
            <Route path="/management-team" element={<Navigate to="/team" replace />} />
            <Route path="/development-team" element={<DevelopmentTeam />} />
            <Route path="/business-associates" element={<BusinessAssociates />} />
            <Route path="/calendar" element={<FamilyCalendar />} />
            <Route path="/family" element={<FamilyHub />} />
            <Route path="/family/records/:kind" element={<FamilyRecords />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/info/:slug" element={<PublicInfoPage />} />
            <Route path="/ai/family-advisor" element={<AIFamilyAdvisor />} />
            <Route path="/ai/family-story" element={<AIFamilyStory />} />
            <Route path="/support" element={<SupportCenter />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/ai-chat" element={<AiChatPage />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/help-center" element={<Navigate to="/help" replace />} />
            <Route path="/privacy-settings" element={<Navigate to="/settings?section=privacy" replace />} />
            <Route path="/features" element={<Features />} />
            <Route path="/demos" element={<DemosPage />} />
            <Route path="/use-cases" element={<UseCases />} />
            <Route path="/solutions" element={<Navigate to="/use-cases" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/time-capsule" element={<TimeCapsule />} />
            <Route path="/franchise" element={<Franchise />} />
            <Route path="/ngo-partnership" element={<NgoPartnership />} />
            <Route path="/community-service" element={<CommunityService />} />
            <Route path="/professional-associate" element={<ProfessionalAssociate />} />
            <Route path="/service/:key" element={<EmbeddedService />} />
            <Route path="/forms/:type" element={<RequestForm />} />
            {USER_PAGES.map(([path, Page]) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute>
                            <FeatureGate>
                                {GATED_PATHS.has(path) ? (
                                    <BiometricGate label={path === "/vault" ? "the Document Vault" : "the Legal Vault"}>
                                        <Page />
                                    </BiometricGate>
                                ) : (
                                    <Page />
                                )}
                            </FeatureGate>
                        </ProtectedRoute>
                    }
                />
            ))}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                {ADMIN_PAGES.map(([path, Page]) => (
                    <Route key={path} path={path} element={<Page />} />
                ))}
            </Route>
            {/* Friendly URL aliases → point to the existing legal / help pages */}
            <Route path="/privacy" element={<Navigate to="/data-security" replace />} />
            <Route path="/terms" element={<Navigate to="/copyright" replace />} />
            <Route path="/contact" element={<Navigate to="/support" replace />} />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </RouteRecovery>
    );
}
